// ── Tree <-> React Flow graph transform ──────────────────────────────────────
//
// `buildGraph` flattens the nested `Task[]` tree into React Flow nodes/edges,
// bookended by synthetic START / WEBHOOK / END nodes, and auto-lays it out
// with dagre (left-to-right). `graphToTasks` converts the graph back into a
// nested `Task[]` tree.
//
// Round-trip strategy: every real (non-synthetic) node carries the
// authoritative original `Task` object on `node.data.__task`. A BRANCH task's
// `__task` already contains its full nested `branches` (including all of
// their tasks), so `graphToTasks` does NOT need to re-derive branch content
// from the graph at all — it only needs to recover the *top-level* sequence
// of tasks (the ones chained directly off START). It does this by following
// the chain of *unlabeled* edges from START: each labeled edge represents a
// branch fan-out and is only ever consulted by the structural tests / future
// editor UI, not by reconstruction. This is deliberately v1-simple: editing
// the nodes *inside* a branch's subgraph (which we do create, for rendering
// and future drag/drop) will not be reflected back into the tree unless a
// later task teaches graphToTasks to rebuild branch contents from the graph
// instead of trusting `__task` wholesale.
import dagre from 'dagre'
import type { Node, Edge } from '@xyflow/react'
import type { FlowNodeData, FlowNodeKind, Task } from './types'
import { isBranchTask } from './types'

export const START_ID = '__start'
export const END_ID = '__end'
export const WEBHOOK_ID = '__webhook'

// `FlowNodeData` is a plain interface (declared in types.ts) with no index
// signature, so it doesn't structurally satisfy React Flow's
// `Record<string, unknown>` constraint on its own. Intersecting with
// `Record<string, unknown>` here (rather than editing types.ts, which is
// owned by an earlier task) satisfies the constraint without changing the
// effective shape.
type FlowNode = Node<FlowNodeData & Record<string, unknown>>

export interface Graph {
  nodes: FlowNode[]
  edges: Edge[]
}

const NODE_WIDTH = 200
const NODE_HEIGHT = 64

function syntheticNode(id: string, kind: Extract<FlowNodeKind, 'START' | 'END' | 'WEBHOOK'>, label: string): FlowNode {
  return {
    id,
    type: kind.toLowerCase(),
    position: { x: 0, y: 0 },
    data: { kind, label },
  }
}

function taskNode(task: Task, branchName?: string): FlowNode {
  const kind: FlowNodeKind = isBranchTask(task) ? 'BRANCH' : 'STEP'
  const data: FlowNodeData & Record<string, unknown> = {
    kind,
    taskId: task.id,
    label: task.name,
    __task: task,
  }
  if (!isBranchTask(task)) {
    data.stepName = task.configuration.name
  }
  if (branchName !== undefined) {
    data.branchName = branchName
  }
  return {
    id: task.id,
    type: kind.toLowerCase(),
    position: { x: 0, y: 0 },
    data,
  }
}

let edgeSeq = 0
function makeEdge(source: string, target: string, label?: string): Edge {
  const edge: Edge = { id: `e${edgeSeq++}_${source}_${target}`, source, target }
  if (label !== undefined) edge.label = label
  return edge
}

/** An edge waiting to be connected into the next node that gets created. */
interface EntryEdge {
  from: string
  label?: string
}

/**
 * Lays out `taskList` as a chain of nodes. `entryEdges` connect into the list's
 * first task (or straight to `contId` when the list is empty). When the list
 * "falls through" — a trailing step with nothing after it, or an empty branch —
 * flow continues to `contId`: the node that follows this list in its parent.
 *
 * A BRANCH fans out one labelled edge per branch, and each branch is laid out
 * with its own continuation set to whatever follows the branch (the next
 * sibling, or the parent continuation when the branch is last). This is what
 * threads a cascade like blocklist → manual KYC → dvs → … → superchoice forward
 * into the shared overall-results branch and on to AML, instead of leaving the
 * deep sub-branches dangling or short-circuiting to the webhook.
 */
function layoutList(
  nodes: FlowNode[],
  edges: Edge[],
  taskList: Task[],
  entryEdges: EntryEdge[],
  contId: string
): void {
  if (taskList.length === 0) {
    // Empty branch (e.g. a "Pass"/"Match" branch with no work): fall straight
    // through to the continuation, preserving the branch label on the edge.
    for (const e of entryEdges) edges.push(makeEdge(e.from, contId, e.label))
    return
  }

  let pending = entryEdges
  taskList.forEach((task, i) => {
    const isLast = i === taskList.length - 1
    // Where flow goes after this task: the next sibling, or the parent
    // continuation when this task is last in the list.
    const nextCont = isLast ? contId : taskList[i + 1].id

    nodes.push(taskNode(task))
    for (const e of pending) edges.push(makeEdge(e.from, task.id, e.label))

    if (isBranchTask(task)) {
      for (const branch of task.configuration.branches) {
        layoutList(nodes, edges, branch.tasks, [{ from: task.id, label: branch.name }], nextCont)
      }
      // The branch node fans out; its branches already reach `nextCont`, so
      // nothing pends onto the following sibling from the branch node itself.
      pending = []
    } else {
      // A step falls through to whatever comes next.
      pending = [{ from: task.id }]
    }
  })

  // A trailing step (list ended on a STEP) falls through to the continuation.
  for (const e of pending) edges.push(makeEdge(e.from, contId, e.label))
}

export function buildGraph(tasks: Task[]): Graph {
  const nodes: FlowNode[] = [syntheticNode(START_ID, 'START', 'Start')]
  const edges: Edge[] = []

  // Lay out the top-level task list; anything that falls through reaches the
  // terminal webhook → end sink.
  layoutList(nodes, edges, tasks, [{ from: START_ID }], WEBHOOK_ID)

  nodes.push(syntheticNode(WEBHOOK_ID, 'WEBHOOK', 'Webhook'))
  nodes.push(syntheticNode(END_ID, 'END', 'End'))
  edges.push(makeEdge(WEBHOOK_ID, END_ID))

  return layout({ nodes, edges })
}

/** Re-runs dagre left-to-right auto-layout over an existing graph (e.g. after edits). */
export function layout(g: Graph): Graph {
  const dagreGraph = new dagre.graphlib.Graph()
  dagreGraph.setDefaultEdgeLabel(() => ({}))
  dagreGraph.setGraph({ rankdir: 'LR', nodesep: 50, ranksep: 80 })

  for (const node of g.nodes) {
    dagreGraph.setNode(node.id, { width: NODE_WIDTH, height: NODE_HEIGHT })
  }
  for (const edge of g.edges) {
    dagreGraph.setEdge(edge.source, edge.target)
  }

  dagre.layout(dagreGraph)

  const nodes = g.nodes.map((node) => {
    const pos = dagreGraph.node(node.id)
    if (!pos) return node
    return {
      ...node,
      position: { x: pos.x - NODE_WIDTH / 2, y: pos.y - NODE_HEIGHT / 2 },
    }
  })

  return { nodes, edges: g.edges }
}

/**
 * Reconstructs the nested `Task[]` tree from a graph. Walks the *unlabeled*
 * chain of edges starting at START; each node visited along the way
 * contributes its `data.__task` (which, for a BRANCH node, already carries
 * its complete nested branch structure). See the module comment above for
 * why this is sufficient for lossless round-trip in v1.
 */
export function graphToTasks(g: Graph): Task[] {
  const nodesById = new Map(g.nodes.map((n) => [n.id, n]))
  const outgoing = (id: string) => g.edges.filter((e) => e.source === id)
  const nextUnlabeled = (id: string): string | undefined =>
    outgoing(id).find((e) => e.label === undefined)?.target

  const tasks: Task[] = []
  let currentId = nextUnlabeled(START_ID)

  while (currentId && currentId !== WEBHOOK_ID && currentId !== END_ID) {
    const node = nodesById.get(currentId)
    const task = node?.data.__task
    if (!task) break
    tasks.push(task)
    currentId = nextUnlabeled(currentId)
  }

  return tasks
}
